import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, History, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import useVisiblePolling from "../../hooks/useVisiblePolling";
import { errorMessage } from "../../lib/errorMessage";
import { SCAN_MODE_LABELS, SCAN_STATUS_LABELS, architectureError, scanIsOpen, shortSha } from "../../lib/architecture";
import { hasPermission } from "../../lib/permissions";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Skeleton } from "../ui/skeleton";
import DiffView from "./DiffView";

const SELECT = "h-10 min-w-0 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora [@media(pointer:coarse)]:h-11";
const STATUS_TONES = {
  requested: "bg-slate-100 text-slate-700 border-slate-300", running: "bg-sky-50 text-sky-800 border-sky-200", published: "bg-emerald-50 text-emerald-800 border-emerald-200",
  unchanged: "bg-slate-100 text-slate-700 border-slate-300", failed: "bg-rose-50 text-rose-800 border-rose-200", cancelled: "bg-slate-100 text-slate-700 border-slate-300",
  rolled_back: "bg-amber-50 text-amber-800 border-amber-200",
};
const dateText = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");

function ScanLine({ scan, canGenerate, onCancel }) {
  const failures = scan.report?.errors || [];
  const checks = (scan.report?.checks || []).filter((c) => !c.ok);
  return (
    <li className="px-3 py-2.5" data-testid="scan-item" data-status={scan.status}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className={`inline-flex rounded border px-1.5 py-0.5 text-2xs font-medium ${STATUS_TONES[scan.status] || STATUS_TONES.requested}`}>{SCAN_STATUS_LABELS[scan.status] || scan.status}</span>
        <span className="text-slate-800">Mise à jour {SCAN_MODE_LABELS[scan.mode]?.toLowerCase() || scan.mode}</span>
        <span className="text-xs text-slate-500">{dateText(scan.finished_at || scan.requested_at)}{scan.requested_by_name ? ` · demandée par ${scan.requested_by_name}` : ""}{scan.commit_sha ? ` · commit ${shortSha(scan.commit_sha)}` : ""}</span>
        {canGenerate && scanIsOpen(scan) ? <Button variant="ghost" size="sm" onClick={() => onCancel(scan)} data-testid="scan-cancel">Annuler</Button> : null}
      </div>
      {scan.status === "failed" || (failures.length && scan.status !== "published") ? (
        <details className="mt-1 text-xs text-slate-700" data-testid="scan-report">
          <summary className="cursor-pointer font-medium text-slate-800 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:leading-[2.75rem]">Pourquoi ça a échoué</summary>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {(failures.length ? failures : checks.map((c) => `${c.code} : ${c.detail}`)).map((f) => <li key={f} className="break-words">{f}</li>)}
          </ul>
          <p className="mt-1 text-slate-500">La dernière version valide reste celle que vous consultez.</p>
        </details>
      ) : null}
      {scan.status === "published" && scan.report?.diff ? (
        <p className="mt-1 text-xs text-slate-600">Relations : + {scan.report.diff.relations_added.toLocaleString("fr-FR")} / − {scan.report.diff.relations_removed.toLocaleString("fr-FR")}{scan.report.scope?.available ? ` · ${scan.report.scope.touched} fichier(s) touchés, ${scan.report.scope.reverse_dependencies} dépendances inverses` : ""}</p>
      ) : null}
    </li>
  );
}

// Versions, updates and comparison (lot CRM-4). Nothing is scanned in the browser or in the application: « Actualiser » records a request that the
// isolated runner picks up; the map moves only once its checks passed, and a failed update leaves the last valid version in place, with the reason.
export default function VersionsTab({ status, current, from, to, onPick, onCompare, onOpenNode, onChanged }) {
  const { user } = useAuth();
  const canGenerate = hasPermission(user, "architecture:generate");
  const canCompare = hasPermission(user, "architecture:compare");
  const canRollback = hasPermission(user, "architecture:admin");
  const versions = status?.versions || [];
  const [scans, setScans] = useState(null);
  const [mode, setMode] = useState("incremental");
  const [busy, setBusy] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [pick, setPick] = useState({ from: from || versions[1]?.snapshot_id || "", to: to || versions[0]?.snapshot_id || "" });
  const [diff, setDiff] = useState({ phase: "idle" });

  const loadScans = useCallback(async () => {
    try {
      const { data } = await api.get("/architecture/scans", { params: { limit: 8 } });
      setScans((previous) => {
        const wasOpen = previous?.some(scanIsOpen);
        if (wasOpen && !data.scans.some(scanIsOpen)) onChanged();
        return data.scans;
      });
    } catch {
      setScans((previous) => previous || []);
    }
  }, [onChanged]);

  useEffect(() => { loadScans(); }, [loadScans]);
  useVisiblePolling(loadScans, 5000, !!scans?.some(scanIsOpen));

  useEffect(() => {
    if (!from || !to) { setDiff({ phase: "idle" }); return undefined; }
    let live = true;
    setDiff({ phase: "loading" });
    api.get("/architecture/diff", { params: { from, to } })
      .then(({ data }) => live && setDiff({ phase: "ready", data }))
      .catch((e) => live && setDiff({ phase: "error", message: architectureError(e, "Impossible de comparer ces deux versions. Réessayez dans quelques instants.") }));
    return () => { live = false; };
  }, [from, to]);

  const request = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/architecture/scans", { mode }, { headers: { "Idempotency-Key": `arch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` } });
      toast.success(data.deduplicated ? "Une mise à jour est déjà en attente ou en cours." : "Mise à jour demandée : la carte sera actualisée dès que l'analyse sera terminée.");
      await loadScans();
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de demander la mise à jour. Réessayez dans quelques instants."));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (scan) => {
    try {
      await api.post(`/architecture/scans/${scan.scan_id}/cancel`);
      toast.success("Mise à jour annulée.");
      await loadScans();
    } catch (e) {
      toast.error(errorMessage(e, "Impossible d'annuler cette mise à jour."));
    }
  };

  const rollback = async () => {
    setBusy(true);
    try {
      await api.post("/architecture/rollback", { snapshot_id: rollbackTarget.snapshot_id, reason: reason.trim() });
      toast.success("La cartographie est revenue à cette version. Rien n'a été supprimé.");
      setRollbackTarget(null);
      setReason("");
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de revenir à cette version."));
    } finally {
      setBusy(false);
    }
  };

  const open = scans?.some(scanIsOpen);
  const samePair = pick.from === pick.to;

  return (
    <div data-testid="arch-versions" className="space-y-5">
      <section className="space-y-3" data-testid="arch-updates">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900"><RefreshCw size={16} aria-hidden />Mises à jour</h3>
            <p className="mt-0.5 max-w-3xl text-sm text-slate-600">La carte se met à jour à chaque fusion sur la branche de développement, et par un scan complet chaque semaine. Une mise à jour qui échoue ne remplace rien : la dernière version valide reste en place.</p>
          </div>
          {canGenerate ? (
            <div className="flex flex-wrap items-center gap-2">
              <select aria-label="Type de mise à jour" value={mode} onChange={(e) => setMode(e.target.value)} className={SELECT} data-testid="scan-mode">
                <option value="incremental">Incrémentale (fichiers modifiés)</option>
                <option value="full">Complète</option>
              </select>
              <Button onClick={request} disabled={busy || open} data-testid="scan-request"><RefreshCw size={16} className="mr-1.5" aria-hidden />Actualiser</Button>
            </div>
          ) : null}
        </div>
        {scans === null ? <Skeleton className="h-16 w-full" /> : scans.length ? (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white" data-testid="scan-list">{scans.map((s) => <ScanLine key={s.scan_id} scan={s} canGenerate={canGenerate} onCancel={cancel} />)}</ul>
        ) : <p className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-600" data-testid="scan-empty">Aucune mise à jour n'a encore été lancée.</p>}
      </section>

      <section className="space-y-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900"><History size={16} aria-hidden />Versions publiées</h3>
          <p className="mt-0.5 text-sm text-slate-600">Les 10 dernières versions. Consulter une ancienne version renvoie tous les chemins et les lignes au commit de cette version.</p>
        </div>
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
          {versions.map((v) => {
            const isViewed = v.snapshot_id === current;
            return (
              <li key={v.snapshot_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5" data-testid="version-item">
                <div className="min-w-0 text-sm">
                  <code className="rounded bg-slate-100 px-1 text-slate-800">{shortSha(v.commit_sha)}</code>
                  {v.is_current ? <span className="ml-2 rounded bg-emerald-50 px-1.5 py-0.5 text-2xs font-medium text-emerald-800" data-testid="version-current">version courante</span> : null}
                  {v.is_latest && !v.is_current ? <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-2xs font-medium text-amber-800">la plus récente</span> : null}
                  <span className="ml-2 text-xs text-slate-500">{v.branch || "branche inconnue"} · {dateText(v.published_at)} · {v.counts?.nodes?.toLocaleString("fr-FR")} éléments</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {isViewed ? <span className="text-xs font-medium text-slate-600">Consultée</span> : <Button variant="outline" size="sm" onClick={() => onPick(v.is_current ? "" : v.snapshot_id)} data-testid="version-open">Consulter</Button>}
                  {canRollback && !v.is_current ? <Button variant="outline" size="sm" onClick={() => { setRollbackTarget(v); setReason(""); }} data-testid="version-rollback">Revenir à cette version</Button> : null}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {canCompare && versions.length > 1 ? (
        <section className="space-y-3" data-testid="arch-compare">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Comparer deux versions</h3>
            <p className="mt-0.5 text-sm text-slate-600">Ce qui a été ajouté, supprimé, renommé ou modifié entre deux versions publiées, avec ce que ces changements peuvent toucher.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex w-full min-w-0 flex-col gap-1 text-xs font-medium text-slate-700 sm:w-auto sm:flex-1 sm:basis-56">De
              <select value={pick.from} onChange={(e) => setPick({ ...pick, from: e.target.value })} className={SELECT} data-testid="compare-from">
                {versions.map((v) => <option key={v.snapshot_id} value={v.snapshot_id}>{shortSha(v.commit_sha)} · {dateText(v.published_at)}</option>)}
              </select>
            </label>
            <label className="flex w-full min-w-0 flex-col gap-1 text-xs font-medium text-slate-700 sm:w-auto sm:flex-1 sm:basis-56">À
              <select value={pick.to} onChange={(e) => setPick({ ...pick, to: e.target.value })} className={SELECT} data-testid="compare-to">
                {versions.map((v) => <option key={v.snapshot_id} value={v.snapshot_id}>{shortSha(v.commit_sha)} · {dateText(v.published_at)}</option>)}
              </select>
            </label>
            <Button className="w-full sm:w-auto" onClick={() => onCompare(pick.from, pick.to)} disabled={samePair} data-testid="compare-run">Comparer</Button>
          </div>
          {samePair ? <p className="text-xs text-slate-500">Choisissez deux versions différentes.</p> : null}
          {diff.phase === "loading" ? <Skeleton className="h-40 w-full" data-testid="diff-loading" /> : null}
          {diff.phase === "error" ? <p role="alert" className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid="diff-error"><AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />{diff.message}</p> : null}
          {diff.phase === "ready" ? <DiffView diff={diff.data} onOpenNode={onOpenNode} onClear={() => onCompare("", "")} /> : null}
        </section>
      ) : null}

      <Dialog open={!!rollbackTarget} onOpenChange={(o) => !o && setRollbackTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revenir à la version {rollbackTarget ? shortSha(rollbackTarget.commit_sha) : ""} ?</DialogTitle>
            <DialogDescription>Cette version redevient celle que tout le monde consulte. Rien n'est supprimé : vous pourrez revenir à la plus récente de la même façon. Le motif est conservé dans le journal.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label htmlFor="rollback-reason" className="text-sm font-medium text-slate-700">Motif</label>
            <input id="rollback-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="Pourquoi revenir en arrière ?" data-testid="rollback-reason"
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-base text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora sm:text-sm" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollbackTarget(null)}>Annuler</Button>
            <Button onClick={rollback} disabled={busy || reason.trim().length < 3} data-testid="rollback-confirm">Revenir à cette version</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
